/**
 * ToolsPanel - the simplified Figma-style bottom toolbar.
 *
 * Layout (single compact row inside the floating dock):
 *   [Select] [Draw-group slot + chevron dropdown] [Measure] [Scale]
 *   | [Undo] [Delete] [Clear] | [Design | Make mode toggle]
 *
 * The segmented Design | Make toggle at the right end switches the app
 * mode (UIManager.setMode via onModeChange). In Make mode the toolbar
 * collapses to a subset: only Select and the mode toggle stay visible.
 *
 * The draw group holds Brush / Leg / Polygon Path / Offset Path / Bezier
 * in a Figma-style dropdown that opens ABOVE the toolbar; the main slot
 * always shows (and re-activates) the group's current tool.
 *
 * A contextual settings strip (Size / Cap / Union) appears as a second
 * row in the dock while the brush or offset tool is active.
 *
 * All tool-switching logic is unchanged from the previous tab-based UI:
 * setTool(id) is still the single entry point (used by the toolbar, the
 * dropdown and Application's keyboard shortcuts).
 */

import { ICONS, phIcon } from '../icons.js';

export class ToolsPanel {
    constructor(application) {
        this.app = application;

        // Tool definitions. Shortcuts match Application.setupKeyboardControls.
        this.tools = [
            {
                id: 'select',
                name: 'Select',
                icon: ICONS.cursor,
                shortcut: 'A',
                tooltip: 'Edit control points on paths'
            },
            {
                id: 'brush',
                name: 'Brush',
                icon: ICONS.paintBrush,
                shortcut: 'D',
                tooltip: 'Produces a single stroke with an offset',
                group: 'draw'
            },
            {
                id: 'leg',
                name: 'Leg',
                icon: ICONS.officeChair,
                shortcut: 'L',
                tooltip: 'Produces a leg shape with its end always parallel to the ground',
                group: 'draw'
            },
            {
                id: 'path',
                name: 'Polygon Path',
                icon: ICONS.polygon,
                shortcut: 'P',
                tooltip: 'Creates control point-based paths with bezier capabilities',
                group: 'draw'
            },
            {
                id: 'offset',
                name: 'Offset Path',
                icon: ICONS.penNib,
                shortcut: 'O',
                tooltip: 'Produces a path with an offset',
                group: 'draw'
            },
            {
                id: 'bezier',
                name: 'Bezier',
                icon: ICONS.bezierCurve,
                shortcut: 'B',
                tooltip: 'Manipulate bezier control points',
                group: 'draw'
            },
            {
                id: 'measure',
                name: 'Measure',
                icon: ICONS.ruler,
                shortcut: '',
                tooltip: 'Measure: drag on the model to read a distance in mm'
            },
            {
                id: 'hand',
                name: 'Hand',
                icon: ICONS.hand,
                shortcut: 'H',
                tooltip: 'Hand: drag the chair or figure — springy while physics plays, free XY move while paused'
            },
            {
                id: 'scale',
                name: 'Scale',
                icon: ICONS.arrowsOut,
                shortcut: '',
                tooltip: 'Scale designs'
            },
            {
                id: 'undo',
                name: 'Undo',
                icon: ICONS.arrowUUpLeft,
                shortcut: 'Ctrl+Z',
                tooltip: 'Reverse last edit',
                action: true
            },
            {
                id: 'clear',
                name: 'Clear',
                icon: ICONS.trash,
                shortcut: '',
                tooltip: 'Clear the whole design (undoable). Use the Delete key to remove a selected shape.',
                action: true
            }
        ];

        this.drawToolIds = this.tools.filter(t => t.group === 'draw').map(t => t.id);

        this.currentTool = 'brush';
        // The draw-group slot remembers the last drawing tool picked
        this.currentDrawTool = 'brush';

        // Tool settings state
        this.brushSize = 40; // Default brush size (matches Java default)
        this.currentCapType = 1; // CAP_ROUND

        // Set by UIManager: called with 'design' | 'make' when the user
        // clicks the mode toggle (UIManager then calls setMode back)
        this.onModeChange = null;
        this.mode = 'design';
        // Elements hidden while Make mode is on (element + restore display)
        this.designOnlyEls = [];

        this.createPanel();
    }

    /**
     * Look up a tool definition by id
     */
    getToolDef(id) {
        return this.tools.find(t => t.id === id);
    }

    /**
     * Create the panel DOM structure
     */
    createPanel() {
        // Main container: toolbar row on top, contextual settings strip below
        const mainContainer = document.createElement('div');
        mainContainer.className = 'tools-panel-container';
        mainContainer.style.cssText = `
            position: relative;
            display: flex;
            flex-direction: column;
            align-items: stretch;
            gap: 0;
        `;

        // --- Toolbar row ---
        const row = document.createElement('div');
        row.className = 'main-toolbar-row';
        row.style.cssText = `
            display: flex;
            align-items: center;
            gap: 4px;
            padding: 8px 10px;
        `;

        // Select
        this.selectBtn = this.createIconButton(this.getToolDef('select'),
            () => this.setTool('select'));
        row.appendChild(this.selectBtn);

        // Draw group: main slot + chevron opening the dropdown
        const drawGroup = this.createDrawGroup();
        row.appendChild(drawGroup);
        this.designOnlyEls.push({ el: drawGroup, display: 'flex' });

        // Measure / Scale
        this.measureBtn = this.createIconButton(this.getToolDef('measure'),
            () => this.setTool('measure'));
        row.appendChild(this.measureBtn);
        this.designOnlyEls.push({ el: this.measureBtn, display: '' });

        // Hand (physics grab) tool
        this.handBtn = this.createIconButton(this.getToolDef('hand'),
            () => this.setTool('hand'));
        row.appendChild(this.handBtn);
        this.designOnlyEls.push({ el: this.handBtn, display: '' });

        this.scaleBtn = this.createIconButton(this.getToolDef('scale'),
            () => this.setTool('scale'));
        row.appendChild(this.scaleBtn);
        this.designOnlyEls.push({ el: this.scaleBtn, display: '' });

        const divider1 = this.createDivider();
        row.appendChild(divider1);
        this.designOnlyEls.push({ el: divider1, display: '' });

        // Actions: Undo / Delete / Clear (route through setTool so the
        // existing act-then-restore-previous-tool logic is preserved)
        for (const id of ['undo', 'clear']) {
            const btn = this.createIconButton(this.getToolDef(id),
                () => this.setTool(id));
            row.appendChild(btn);
            this.designOnlyEls.push({ el: btn, display: '' });
        }

        const divider2 = this.createDivider();
        row.appendChild(divider2);
        this.designOnlyEls.push({ el: divider2, display: '' });

        // Design | Make segmented mode toggle (right end of the dock)
        this.modeToggle = this.createModeToggle();
        row.appendChild(this.modeToggle);

        // --- Contextual settings strip (second row) ---
        this.settingsPanel = this.createSettingsPanel();

        mainContainer.appendChild(row);

        // The settings strip lives in its OWN floating card above the
        // toolbar (positioned by updateSettingsCardPosition) so opening it
        // never resizes or moves the dock
        this.settingsCard = document.createElement('div');
        this.settingsCard.id = 'tool-settings-card';
        this.settingsCard.style.cssText = `
            position: absolute;
            display: none;
            background: rgba(255, 255, 255, 0.86);
            backdrop-filter: blur(18px) saturate(1.4);
            -webkit-backdrop-filter: blur(18px) saturate(1.4);
            border: 1px solid rgba(0, 0, 0, 0.08);
            border-radius: 12px;
            box-shadow: 0 8px 30px rgba(0, 0, 0, 0.14), 0 2px 8px rgba(0, 0, 0, 0.06);
            padding: 5px 12px;
            z-index: 60;
        `;
        this.settingsCard.appendChild(this.settingsPanel);
        document.getElementById('canvas-container')?.appendChild(this.settingsCard);

        // Dropdown menu for the draw group (opens above the dock)
        this.drawMenu = this.createDrawMenu();
        mainContainer.appendChild(this.drawMenu);

        this.panel = mainContainer;

        // Close the dropdown on any outside click
        this._onDocPointerDown = (e) => {
            if (!this.drawMenu.contains(e.target) &&
                !this.drawGroupEl.contains(e.target)) {
                this.closeDrawMenu();
            }
        };
        document.addEventListener('pointerdown', this._onDocPointerDown, true);

        // Initialize button + settings visibility (brush is the default tool)
        this.updateButtonStates();
        this.updateSettingsVisibility();
    }

    /**
     * Get the panel element
     */
    getElement() {
        return this.panel;
    }

    /**
     * Thin vertical divider between toolbar groups
     */
    createDivider() {
        const div = document.createElement('div');
        div.className = 'toolbar-divider';
        div.style.cssText = `
            width: 1px;
            height: 24px;
            background: rgba(0, 0, 0, 0.12);
            margin: 0 4px;
            flex-shrink: 0;
        `;
        return div;
    }

    /**
     * A 36px square icon button (Phosphor icon inlined for currentColor)
     */
    createIconButton(tool, onClick) {
        const button = document.createElement('button');
        button.className = 'ph-btn';
        button.dataset.tool = tool.id;
        button.title = tool.shortcut
            ? `${tool.name} (${tool.shortcut})\n${tool.tooltip}`
            : `${tool.name}\n${tool.tooltip}`;
        button.appendChild(phIcon(tool.icon, 20));
        button.addEventListener('click', onClick);
        return button;
    }

    /**
     * Draw group: one slot showing the group's current tool, plus a small
     * chevron opening the tool dropdown
     */
    createDrawGroup() {
        const group = document.createElement('div');
        group.className = 'draw-group';
        group.style.cssText = `
            display: flex;
            align-items: center;
            gap: 0;
        `;

        const drawTool = this.getToolDef(this.currentDrawTool);

        this.drawSlotBtn = document.createElement('button');
        this.drawSlotBtn.className = 'ph-btn';
        this.drawSlotBtn.dataset.tool = 'draw-slot';
        this.drawSlotBtn.title = `${drawTool.name} (${drawTool.shortcut})\n${drawTool.tooltip}`;
        this.drawSlotIcon = phIcon(drawTool.icon, 20);
        this.drawSlotBtn.appendChild(this.drawSlotIcon);
        this.drawSlotBtn.addEventListener('click', () => {
            this.setTool(this.currentDrawTool);
        });
        group.appendChild(this.drawSlotBtn);

        this.drawChevronBtn = document.createElement('button');
        this.drawChevronBtn.className = 'ph-btn ph-chevron';
        this.drawChevronBtn.title = 'Drawing tools';
        this.drawChevronBtn.appendChild(phIcon(ICONS.caretDown, 10));
        this.drawChevronBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.toggleDrawMenu();
        });
        group.appendChild(this.drawChevronBtn);

        this.drawGroupEl = group;
        return group;
    }

    /**
     * Figma-style dropdown listing the draw group's tools
     * (dark background, rows of [icon, name, shortcut], check on active)
     */
    createDrawMenu() {
        const menu = document.createElement('div');
        menu.className = 'draw-tool-menu';
        menu.style.cssText = `
            position: absolute;
            bottom: calc(100% + 10px);
            left: 8px;
            display: none;
            flex-direction: column;
            min-width: 220px;
            padding: 6px;
            background: rgba(30, 30, 32, 0.95);
            backdrop-filter: blur(12px);
            -webkit-backdrop-filter: blur(12px);
            border-radius: 12px;
            box-shadow: 0 12px 32px rgba(0, 0, 0, 0.35), 0 2px 8px rgba(0, 0, 0, 0.2);
            z-index: 100;
        `;

        this.drawMenuRows = {};
        for (const id of this.drawToolIds) {
            const tool = this.getToolDef(id);
            const rowEl = document.createElement('button');
            rowEl.className = 'draw-menu-row';
            rowEl.title = tool.tooltip;
            rowEl.style.cssText = `
                display: flex;
                align-items: center;
                gap: 10px;
                padding: 8px 10px;
                border: none;
                border-radius: 8px;
                background: transparent;
                color: #fff;
                font-size: 13px;
                cursor: pointer;
                text-align: left;
            `;

            // Check mark (visible on the active tool)
            const check = phIcon(ICONS.check, 14);
            check.style.visibility = 'hidden';
            rowEl.appendChild(check);

            rowEl.appendChild(phIcon(tool.icon, 18));

            const name = document.createElement('span');
            name.textContent = tool.name;
            name.style.cssText = 'flex: 1; white-space: nowrap;';
            rowEl.appendChild(name);

            const shortcut = document.createElement('span');
            shortcut.textContent = tool.shortcut;
            shortcut.style.cssText = 'color: rgba(255,255,255,0.5); font-size: 12px;';
            rowEl.appendChild(shortcut);

            rowEl.addEventListener('mouseenter', () => {
                rowEl.style.background = 'rgba(255, 255, 255, 0.1)';
            });
            rowEl.addEventListener('mouseleave', () => {
                rowEl.style.background = 'transparent';
            });
            rowEl.addEventListener('click', () => {
                this.closeDrawMenu();
                this.setTool(id);
            });

            this.drawMenuRows[id] = { rowEl, check };
            menu.appendChild(rowEl);
        }

        return menu;
    }

    /**
     * Open/close the draw-tool dropdown (anchored above the draw group)
     */
    toggleDrawMenu() {
        if (this.drawMenu.style.display === 'flex') {
            this.closeDrawMenu();
        } else {
            // Anchor the menu's left edge to the draw group
            this.drawMenu.style.left = `${this.drawGroupEl.offsetLeft}px`;
            // Check marks reflect the group's current tool
            for (const [id, refs] of Object.entries(this.drawMenuRows)) {
                refs.check.style.visibility =
                    id === this.currentDrawTool ? 'visible' : 'hidden';
            }
            this.drawMenu.style.display = 'flex';
        }
    }

    closeDrawMenu() {
        this.drawMenu.style.display = 'none';
    }

    /**
     * The segmented Design | Make mode toggle (like Figma's Design/Dev
     * switch). Clicking a segment reports the mode via onModeChange;
     * UIManager then calls setMode() back to update visuals + subset.
     */
    createModeToggle() {
        const wrap = document.createElement('div');
        wrap.className = 'mode-toggle';
        wrap.style.cssText = `
            display: flex;
            align-items: center;
            gap: 2px;
            background: rgba(0, 0, 0, 0.06);
            border-radius: 8px;
            padding: 2px;
            flex-shrink: 0;
        `;

        const makeSegment = (id, label, icon, tooltip) => {
            const btn = document.createElement('button');
            btn.className = 'mode-toggle-btn';
            btn.dataset.mode = id;
            btn.title = tooltip;
            btn.style.cssText = `
                display: flex;
                align-items: center;
                gap: 6px;
                height: 30px;
                padding: 0 12px;
                border: none;
                border-radius: 6px;
                background: transparent;
                color: #555;
                font-size: 12px;
                font-weight: 600;
                cursor: pointer;
                flex-shrink: 0;
                transition: background 0.15s ease, color 0.15s ease;
            `;
            btn.appendChild(phIcon(icon, 16));
            const span = document.createElement('span');
            span.textContent = label;
            btn.appendChild(span);
            btn.addEventListener('click', () => {
                if (this.mode !== id && this.onModeChange) {
                    this.onModeChange(id);
                }
            });
            return btn;
        };

        this.designModeBtn = makeSegment('design', 'Design', ICONS.pencilSimple,
            'Design mode - sketch and edit the chair');
        this.makeModeBtn = makeSegment('make', 'Make', ICONS.printer,
            'Make mode - cut patterns, 3D print and export');
        wrap.appendChild(this.designModeBtn);
        wrap.appendChild(this.makeModeBtn);

        this.refreshModeToggle();
        return wrap;
    }

    /**
     * Reflect the current mode on the toggle segments
     * (active = accent background + white text)
     */
    refreshModeToggle() {
        for (const btn of [this.designModeBtn, this.makeModeBtn]) {
            if (!btn) continue;
            const active = btn.dataset.mode === this.mode;
            btn.style.background = active ? 'var(--accent)' : 'transparent';
            btn.style.color = active ? '#fff' : '#555';
        }
    }

    /**
     * Called by UIManager when the app mode changes: update the toggle
     * visuals and collapse/restore the toolbar. In Make mode only Select
     * and the mode toggle remain; the active tool falls back to Select
     * (restored on return to Design mode).
     */
    setMode(mode) {
        if (this.mode === mode) return;
        this.mode = mode;
        this.refreshModeToggle();

        const makeMode = mode === 'make';
        this.closeDrawMenu();
        for (const { el, display } of this.designOnlyEls) {
            // visibility (not display) so the toolbar keeps the SAME width
            // and position in both modes - controls just grey out of view
            el.style.display = display;
            el.style.visibility = makeMode ? 'hidden' : 'visible';
            el.style.pointerEvents = makeMode ? 'none' : '';
        }

        if (makeMode) {
            this._preMakeTool = this.currentTool;
            if (this.currentTool !== 'select') this.setTool('select');
        } else if (this._preMakeTool) {
            if (this._preMakeTool !== this.currentTool) {
                this.setTool(this._preMakeTool);
            }
            this._preMakeTool = null;
        }
    }

    /**
     * Highlight the buttons for the current tool (select / draw slot /
     * measure / scale) and swap the draw slot icon
     */
    updateButtonStates() {
        const setActive = (button, active) => {
            if (!button) return;
            button.classList.toggle('active', active);
        };

        setActive(this.selectBtn, this.currentTool === 'select');
        setActive(this.measureBtn, this.currentTool === 'measure');
        setActive(this.handBtn, this.currentTool === 'hand');
        setActive(this.scaleBtn, this.currentTool === 'scale');

        // Draw slot: shows the group's current tool, highlighted when armed
        const isDraw = this.drawToolIds.includes(this.currentTool);
        setActive(this.drawSlotBtn, isDraw);

        const drawTool = this.getToolDef(this.currentDrawTool);
        const newIcon = phIcon(drawTool.icon, 20);
        this.drawSlotBtn.replaceChild(newIcon, this.drawSlotIcon);
        this.drawSlotIcon = newIcon;
        this.drawSlotBtn.title = `${drawTool.name} (${drawTool.shortcut})\n${drawTool.tooltip}`;
    }

    /**
     * Set the active tool
     */
    setTool(toolId) {
        const previousTool = this.currentTool;
        this.currentTool = toolId;

        // Remember the last drawing tool for the draw-group slot
        if (this.drawToolIds.includes(toolId)) {
            this.currentDrawTool = toolId;
        }

        // Picking a drawing tool leaves any active camera mode
        if (this.app.setCameraMode && this.app.activeCameraMode) {
            this.app.setCameraMode(null);
        }

        // Update button styles
        this.updateButtonStates();

        // Update settings panel visibility
        this.updateSettingsVisibility();

        // Map tool ID to sketch plane tool
        const toolMap = {
            'brush': 'brush',
            'path': 'pen',
            'select': 'select',
            'leg': 'leg',
            'offset': 'offset',
            'bezier': 'bezier',
            'measure': 'measure',
            'hand': 'hand',
            'scale': 'scale',
            'undo': 'undo',
            'delete': 'delete',
            'clear': 'clear'
        };

        const mappedTool = toolMap[toolId] || toolId;

        // Update application tool
        if (this.app.sketchPlane) {
            if (mappedTool === 'undo' || mappedTool === 'delete' || mappedTool === 'clear') {
                if (mappedTool === 'undo') {
                    this.app.undo();
                } else if (mappedTool === 'delete') {
                    if (!this.app.sketchPlane.deleteSelected()) {
                        console.log('Nothing selected to delete');
                    }
                } else {
                    this.app.clearAll();
                }
                // These are actions, not modes - restore the previous tool
                if (previousTool && !['undo', 'delete', 'clear'].includes(previousTool)) {
                    this.setTool(previousTool);
                }
                return;
            } else {
                this.app.sketchPlane.setTool(mappedTool);
                // Leaving the measure tool clears its measurement overlay
                if (mappedTool !== 'measure') {
                    this.app.clearMeasurement?.();
                }
                console.log('Tool changed to:', mappedTool);
            }
        }

        // Handle layer selection for leg tool
        if (this.app.basePlanes && this.app.basePlanes.length > 0) {
            // Switching TO leg tool: save current selection and select outer layers
            if (toolId === 'leg' && previousTool !== 'leg') {
                // Save current layer selection
                this.savedLayerSelection = new Set(this.app.selectedBasePlaneIndices);

                // Auto-select outer layers
                this.app.selectedBasePlaneIndices.clear();
                this.app.selectedBasePlaneIndices.add(0); // First layer
                this.app.selectedBasePlaneIndices.add(this.app.basePlanes.length - 1); // Last layer

                // Update visualization to show selected layers
                this.app.clearPlaneVisualizations();
                this.app.visualizeBasePlanes();

                console.log(`Auto-selected outer layers: 0 and ${this.app.basePlanes.length - 1}`);
                console.log(`Saved previous selection:`, Array.from(this.savedLayerSelection));
            }
            // Switching FROM leg tool: restore previous selection
            else if (previousTool === 'leg' && toolId !== 'leg' && this.savedLayerSelection) {
                // Restore previous layer selection
                this.app.selectedBasePlaneIndices.clear();
                this.savedLayerSelection.forEach(index => {
                    this.app.selectedBasePlaneIndices.add(index);
                });

                // Update visualization
                this.app.clearPlaneVisualizations();
                this.app.visualizeBasePlanes();

                console.log(`Restored previous layer selection:`, Array.from(this.app.selectedBasePlaneIndices));
                this.savedLayerSelection = null; // Clear saved selection
            }
        }
    }

    /**
     * Create the tool settings panel
     * Shows/hides based on selected tool
     */
    /**
     * Centre the floating settings card just above the toolbar dock
     */
    positionSettingsCard() {
        const dock = document.getElementById('bottom-toolbar');
        const host = document.getElementById('canvas-container');
        if (!dock || !host || !this.settingsCard) return;
        const d = dock.getBoundingClientRect();
        const h = host.getBoundingClientRect();
        const cardW = this.settingsCard.offsetWidth || 420;
        this.settingsCard.style.left = `${d.left - h.left + d.width / 2 - cardW / 2}px`;
        this.settingsCard.style.top = `${d.top - h.top - this.settingsCard.offsetHeight - 10}px`;
    }

    createSettingsPanel() {
        const container = document.createElement('div');
        container.className = 'tool-settings-panel panel';
        container.style.cssText = `
            display: none;
            flex-direction: row;
            align-items: center;
            justify-content: center;
            gap: 8px;
        `;

        // Brush size section
        const brushSizeSection = this.createBrushSizeControl();
        container.appendChild(brushSizeSection);

        // Cap style section
        const capStyleSection = this.createCapStyleControls();
        container.appendChild(capStyleSection);

        return container;
    }

    /**
     * Create brush size slider control
     */
    createBrushSizeControl() {
        const section = document.createElement('div');
        section.className = 'brush-size-section';
        section.style.cssText = `
            display: flex;
            align-items: center;
            gap: 8px;
            flex: 0 0 auto;
        `;

        // Label
        const label = document.createElement('label');
        label.textContent = 'Size';
        label.style.cssText = `
            font-size: 12px;
            font-weight: 600;
            color: #333;
            white-space: nowrap;
        `;

        // Slider container (slider + value display)
        const sliderContainer = document.createElement('div');
        sliderContainer.style.cssText = `
            display: flex;
            align-items: center;
            gap: 8px;
            flex: 1;
        `;

        // Slider
        const slider = document.createElement('input');
        slider.type = 'range';
        slider.min = '1';
        slider.max = '100';
        slider.value = this.brushSize.toString();
        slider.style.cssText = `
            width: 78px;
            flex: 0 0 auto;
            background: linear-gradient(to right, var(--accent) 0%, var(--accent) ${this.brushSize}%, #ddd ${this.brushSize}%, #ddd 100%);
        `;

        // Value display
        const valueDisplay = document.createElement('span');
        valueDisplay.textContent = this.brushSize.toString();
        valueDisplay.style.cssText = `
            font-size: 14px;
            font-weight: 600;
            color: var(--accent-strong);
            min-width: 32px;
            text-align: right;
            margin-right: 8px;
        `;

        // Slider change handler
        slider.addEventListener('input', (e) => {
            const value = parseInt(e.target.value);
            this.setBrushSize(value);
            valueDisplay.textContent = value.toString();

            // Update slider background
            slider.style.background = `linear-gradient(to right, var(--accent) 0%, var(--accent) ${value}%, #ddd ${value}%, #ddd 100%)`;
        });

        sliderContainer.appendChild(slider);
        sliderContainer.appendChild(valueDisplay);

        section.appendChild(label);
        section.appendChild(sliderContainer);

        this.brushSizeSlider = slider;
        this.brushSizeValue = valueDisplay;

        return section;
    }

    /**
     * Create cap style toggle buttons
     */
    createCapStyleControls() {
        const section = document.createElement('div');
        section.className = 'cap-style-section';
        section.style.cssText = `
            display: flex;
            align-items: center;
            gap: 8px;
        `;

        // Label
        const label = document.createElement('label');
        label.textContent = 'Cap';
        label.style.cssText = `
            font-size: 12px;
            font-weight: 600;
            color: #333;
            white-space: nowrap;
        `;

        // Button container
        const buttonContainer = document.createElement('div');
        buttonContainer.style.cssText = `
            display: flex;
            gap: 8px;
        `;

        // Cap types (matching Java constants and SketchSpline.js)
        const capTypes = [
            { id: 1, label: 'Round', icon: '⬤' },
            { id: 2, label: 'Butt', icon: '⬜' },
            { id: 4, label: 'Parallel', icon: '║' }
        ];

        this.capButtons = {};

        capTypes.forEach(capType => {
            const button = document.createElement('button');
            button.className = 'cap-style-button';
            button.textContent = capType.icon;
            button.title = capType.label;
            button.style.cssText = `
                padding: 3px 7px;
                border: 2px solid #ddd;
                border-radius: 6px;
                background: white;
                cursor: pointer;
                font-size: 14px;
                transition: background 0.15s ease, color 0.15s, border-color 0.15s;
            `;

            // Highlight current selection
            if (capType.id === this.currentCapType) {
                button.style.borderColor = 'var(--accent)';
                button.style.background = 'var(--accent-tint)';
            }

            button.addEventListener('click', () => {
                this.setCapType(capType.id);
            });

            this.capButtons[capType.id] = button;
            buttonContainer.appendChild(button);
        });

        section.appendChild(label);
        section.appendChild(buttonContainer);

        return section;
    }

    /**
     * Set brush size
     * @param {number} size - Brush size (1-100)
     */
    setBrushSize(size) {
        this.brushSize = Math.max(1, Math.min(100, size));

        // Update sketch plane
        if (this.app.sketchPlane) {
            this.app.sketchPlane.brushWidth = this.brushSize;

            // If there's a current spline being drawn, update its offset size
            if (this.app.sketchPlane.currentSpline) {
                this.app.sketchPlane.currentSpline.setOffsetSize(this.brushSize);
            }

            this.app.sketchPlane.render();
        }

        console.log('Brush size set to:', this.brushSize);
    }

    /**
     * Set cap type
     * @param {number} capType - Cap type constant (1=ROUND, 2=BUTT, 4=PARALLEL)
     */
    setCapType(capType) {
        this.currentCapType = capType;

        // Update button styles
        Object.entries(this.capButtons).forEach(([id, button]) => {
            if (parseInt(id) === capType) {
                button.style.borderColor = 'var(--accent)';
                button.style.background = 'var(--accent-tint)';
            } else {
                button.style.borderColor = '#ddd';
                button.style.background = 'white';
            }
        });

        // Update sketch plane - set cap type for future splines
        if (this.app.sketchPlane) {
            this.app.sketchPlane.currentCapType = capType;

            // If there's a current spline being drawn, update its cap type
            if (this.app.sketchPlane.currentSpline) {
                this.app.sketchPlane.currentSpline.capType = capType;
                this.app.sketchPlane.currentSpline.build();
                this.app.sketchPlane.render();
            }
        }

        console.log('Cap type set to:', capType);
    }

    /**
     * Show or hide settings panel based on tool
     */
    updateSettingsVisibility() {
        const showSettings = this.currentTool === 'brush' || this.currentTool === 'offset';
        this.settingsPanel.style.display = showSettings ? 'flex' : 'none';
        if (this.settingsCard) {
            this.settingsCard.style.display = showSettings ? 'block' : 'none';
            if (showSettings) {
                // Position twice: immediately (best guess) and next frame
                // once layout has real dimensions - first open otherwise
                // measures offsetHeight 0 and lands under the toolbar
                this.positionSettingsCard();
                requestAnimationFrame(() => this.positionSettingsCard());
            }
        }
    }

    /**
     * Update panel state
     */
    update() {
        // Update based on current tool
        if (this.app.sketchPlane) {
            const currentTool = this.app.sketchPlane.tool;

            // Reverse map from sketch plane tool to UI tool
            const reverseMap = {
                'brush': 'brush',
                'pen': 'path',
                'select': 'select',
                'leg': 'leg',
                'offset': 'offset',
                'bezier': 'bezier',
                'measure': 'measure',
                'scale': 'scale'
            };

            const uiTool = reverseMap[currentTool];
            if (uiTool && uiTool !== this.currentTool) {
                this.setTool(uiTool);
            }
        }
    }

    /**
     * Cleanup
     */
    dispose() {
        document.removeEventListener('pointerdown', this._onDocPointerDown, true);
    }
}
